import React, { memo } from 'react';
import { Handle, Position, NodeProps } from 'reactflow';

interface RouterNodeData {
    hostname: string;
    role: string;
    nodeType: 'router' | 'route-reflector';
    isLocal: boolean;
    site: string;
    pathHighlight?: 'source';
    sptCost?: number;
    sptRoot?: boolean;
    excluded?: boolean;
    p2pActive?: boolean;
    p2pOnPath?: boolean;
    p2pRole?: 'source' | 'target';
    /** Optional health status. Defaults to "active". */
    health?: 'active' | 'degraded' | 'down';
    /** Flex-algo filter is active anywhere on the canvas. When true,
     *  non-participants are dimmed and participants get a subtle
     *  brand-coloured ring. */
    algoFilterActive?: boolean;
    algoIsParticipant?: boolean;
}

const ROUTER_ACCENT = 'var(--color-semantic-fg-brand-primary)';
const RR_ACCENT = 'var(--color-semantic-fg-danger)';

const STATUS_COLORS: Record<NonNullable<RouterNodeData['health']>, string> = {
    active: 'var(--color-semantic-text-success, #16a34a)',
    degraded: 'var(--color-semantic-text-warning, #f59e0b)',
    down: 'var(--color-semantic-text-danger, #dc2626)',
};

const RouterIcon: React.FC<{ color: string; size?: number }> = ({
    color,
    size = 28,
}) => (
    <svg width={size} height={size} viewBox="0 0 1024 1024" fill="none">
        <path
            d="M77 403.4v228.5c1.5 93.7 195.7 183.5 435 183.5s433.4-89.8
               435-183.5V403.4H77z"
            fill={color}
            opacity="0.25"
        />
        <path
            d="M947 402.7c0 99.4-194.8 194-435 194s-435-94.6-435-194
               194.8-180 435-180 435 80.5 435 180z"
            fill={color}
            opacity="0.5"
        />
        <path
            d="M474.1 311.4H503l0.1 63.2h29.5l-0.7-63.2h28.9l-43.7-75.1z
               M533 417.2h-29.9l0.1 73.9h-30.6l46.2 75.2 45.5-75.2h-30.6z
               M654.5 380.9l-1.4-30-72.1 45 76.4 45.1-1.4-30h126.2l-2.6-30.1z
               M381.1 380.9h-125l-2.3 30.1H380l-1.1 30 75.9-45.1-72.5-45z"
            fill="white"
        />
    </svg>
);

const RRIcon: React.FC<{ color: string; size?: number }> = ({
    color,
    size = 28,
}) => (
    <svg width={size} height={size} viewBox="0 0 1024 1024" fill="none">
        <path
            d="M77 403.4v228.5c1.5 93.7 195.7 183.5 435 183.5s433.4-89.8
               435-183.5V403.4H77z"
            fill={color}
            opacity="0.25"
        />
        <path
            d="M947 402.7c0 99.4-194.8 194-435 194s-435-94.6-435-194
               194.8-180 435-180 435 80.5 435 180z"
            fill={color}
            opacity="0.5"
        />
        <circle cx="512" cy="400" r="100" stroke="white"
            strokeWidth="24" fill="none" />
        <circle cx="512" cy="400" r="40" fill="white" opacity="0.8" />
        <path
            d="M512 250 L512 300 M512 500 L512 550
               M362 400 L412 400 M612 400 L662 400"
            stroke="white" strokeWidth="24" strokeLinecap="round"
        />
        <path d="M490 268 L512 240 L534 268" stroke="white"
            strokeWidth="20" strokeLinecap="round" fill="none" />
        <path d="M490 532 L512 560 L534 532" stroke="white"
            strokeWidth="20" strokeLinecap="round" fill="none" />
    </svg>
);

const P2P_SRC_COLOR = '#ff4089';
const P2P_TGT_COLOR = '#fbbf24';

const RouterNode: React.FC<NodeProps<RouterNodeData>> = ({ data }) => {
    const isRR = data.nodeType === 'route-reflector';
    const accent = isRR ? RR_ACCENT : ROUTER_ACCENT;
    const statusColor = STATUS_COLORS[data.health ?? 'active'];
    const isPathSource = data.pathHighlight === 'source';
    const hasSptCost = data.sptCost != null;
    const p2pDimmed = data.p2pActive && !data.p2pOnPath;
    const algoDimmed =
        data.algoFilterActive === true && data.algoIsParticipant !== true;
    const algoActive =
        data.algoFilterActive === true && data.algoIsParticipant === true;

    return (
        <div
            // Cursor is owned by the global <style> block in TopologyGraph
            // (`.react-flow__node-routerNode` → pointer / `.dragging` →
            // grabbing). We removed the local `cursor-pointer` utility so
            // the global rule has a single source of truth — the dragging
            // cursor swap was being short-circuited by the static class.
            className="relative flex flex-col items-center group"
            style={{
                minWidth: 78,
                opacity: data.excluded
                    ? 0.3
                    : p2pDimmed
                    ? 0.25
                    : algoDimmed
                    ? 0.2
                    : 1,
                transition: 'opacity 0.2s',
            }}
        >
            {/* P2P source/target ring */}
            {data.p2pRole && (
                <div
                    className="absolute -inset-2 rounded-2xl border-2 z-0"
                    style={{
                        borderColor:
                            data.p2pRole === 'source'
                                ? P2P_SRC_COLOR
                                : P2P_TGT_COLOR,
                        boxShadow: `0 0 14px ${
                            data.p2pRole === 'source'
                                ? P2P_SRC_COLOR
                                : P2P_TGT_COLOR
                        }`,
                        animation: 'pulse 2s cubic-bezier(0.4,0,0.6,1) infinite',
                    }}
                />
            )}
            {/* Path mode selection ring */}
            {isPathSource && !data.p2pRole && (
                <div
                    className="absolute -inset-2 rounded-2xl border-2 animate-pulse z-0"
                    style={{
                        borderColor: 'var(--color-semantic-text-success)',
                        boxShadow:
                            '0 0 12px var(--color-semantic-text-success)',
                    }}
                />
            )}
            {/* Flex-algo participant ring (low priority — yields to
                P2P / pathSource / SPT-root visuals). */}
            {algoActive && !data.p2pRole && !isPathSource && (
                <div
                    className="absolute -inset-1.5 rounded-2xl border z-0"
                    style={{
                        borderColor:
                            'var(--color-semantic-fg-brand-primary)',
                        boxShadow:
                            '0 0 10px var(--color-semantic-fg-brand-primary)',
                        opacity: 0.55,
                    }}
                />
            )}

            {/* ReactFlow handles — invisible anchors */}
            <Handle
                id="top"
                type="target"
                position={Position.Top}
                className="!w-1.5 !h-1.5 !bg-transparent !border-0"
            />
            <Handle
                id="top-src"
                type="source"
                position={Position.Top}
                className="!w-1.5 !h-1.5 !bg-transparent !border-0"
            />
            <Handle
                id="bottom"
                type="source"
                position={Position.Bottom}
                className="!w-1.5 !h-1.5 !bg-transparent !border-0"
            />
            <Handle
                id="bottom-tgt"
                type="target"
                position={Position.Bottom}
                className="!w-1.5 !h-1.5 !bg-transparent !border-0"
            />
            <Handle
                id="left"
                type="target"
                position={Position.Left}
                className="!w-1.5 !h-1.5 !bg-transparent !border-0"
            />
            <Handle
                id="left-src"
                type="source"
                position={Position.Left}
                className="!w-1.5 !h-1.5 !bg-transparent !border-0"
            />
            <Handle
                id="right"
                type="source"
                position={Position.Right}
                className="!w-1.5 !h-1.5 !bg-transparent !border-0"
            />
            <Handle
                id="right-tgt"
                type="target"
                position={Position.Right}
                className="!w-1.5 !h-1.5 !bg-transparent !border-0"
            />

            {/* Card */}
            <div
                className="relative flex flex-col items-center overflow-hidden
                    rounded-md border border-semantic-border-secondary
                    shadow-sm transition-all duration-200
                    group-hover:shadow-md group-hover:scale-[1.03]"
                style={{
                    minWidth: 78,
                    maxWidth: 96,
                    backgroundColor: 'var(--color-semantic-bg-primary, #ffffff)',
                }}
            >
                {/* Top accent stripe */}
                <div
                    className="w-full shrink-0"
                    style={{ background: accent, height: 3 }}
                />

                {/* Status dot at top-right */}
                <span
                    className="absolute top-1.5 right-1.5 rounded-full"
                    style={{
                        width: 7,
                        height: 7,
                        background: statusColor,
                        boxShadow: '0 0 0 1.5px white',
                    }}
                />

                {data.isLocal && (
                    <span
                        className="absolute top-1.5 left-1.5 text-[7px] font-bold
                            uppercase tracking-wider text-white
                            px-1.5 py-0.5 rounded-full
                            bg-semantic-bg-danger-bold z-10"
                    >
                        local
                    </span>
                )}

                <div className="flex flex-col items-center px-3 pt-2.5 pb-2 gap-0.5">
                    {/* Icon */}
                    <div
                        className="flex items-center justify-center w-10 h-10
                            rounded-full bg-semantic-bg-secondary"
                    >
                        {isRR ? (
                            <RRIcon color={accent} size={28} />
                        ) : (
                            <RouterIcon color={accent} size={28} />
                        )}
                    </div>

                    {/* Hostname */}
                    <span
                        className="text-[11px] font-bold font-mono mt-1
                            text-semantic-text-primary text-center leading-tight"
                    >
                        {data.role}
                    </span>

                    {/* Type label */}
                    <span
                        className="text-[8px] font-semibold uppercase
                            tracking-[0.08em] text-semantic-text-secondary"
                    >
                        {isRR ? 'REFLECT.' : 'ROUTER'}
                    </span>
                </div>
            </div>

            {/* SPT cost badge */}
            {hasSptCost && !data.p2pActive && (
                <span
                    className="text-[9px] font-bold px-1.5 py-0.5 rounded-full
                        mt-1 leading-none"
                    style={{
                        background: data.sptRoot
                            ? 'var(--color-semantic-bg-danger-bold)'
                            : 'var(--color-semantic-bg-brand-primary)',
                        color: data.sptRoot
                            ? 'white'
                            : 'var(--color-semantic-text-brand)',
                    }}
                >
                    {data.sptRoot ? 'ROOT' : `cost ${data.sptCost}`}
                </span>
            )}

            {/* P2P role badge */}
            {data.p2pRole && (
                <span
                    className="text-[9px] font-bold px-2 py-0.5 rounded-full
                        mt-1 leading-none uppercase tracking-wider"
                    style={{
                        background:
                            data.p2pRole === 'source'
                                ? P2P_SRC_COLOR
                                : P2P_TGT_COLOR,
                        color: data.p2pRole === 'source' ? 'white' : '#1a1a2e',
                    }}
                >
                    {data.p2pRole}
                </span>
            )}

            {/* Excluded strike-through */}
            {data.excluded && (
                <div
                    className="absolute inset-0 flex items-center justify-center"
                    style={{ pointerEvents: 'none' }}
                >
                    <div
                        style={{
                            width: '120%',
                            height: 2,
                            background: 'var(--color-semantic-text-danger)',
                            transform: 'rotate(-30deg)',
                        }}
                    />
                </div>
            )}
        </div>
    );
};

export default memo(RouterNode);
